import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axiosInstance from '../../../shared/services/api/axiosInstance';
import Icon from '../../../shared/components/Icon';
import { X, Monitor, Tablet, Smartphone } from 'lucide-react';

// ------------------------------------------------------------------
// HeatmapVisualization
//
// Coordinate accuracy strategy:
//   All click positions are stored as percentages of the user's own
//   document dimensions. We re-render the site in an iframe whose
//   width = mode_viewport_width returned by the API (the most common
//   width among all events for this page/filter). This minimises the
//   layout-reflow delta between where users actually clicked and where
//   the dots appear on the preview.
//
// Scroll fix:
//   CSS transform: scale() does NOT affect layout dimensions.  We
//   therefore wrap the scaled block inside a container that is sized
//   to the VISUAL (post-scale) dimensions, making the scroll area
//   match exactly what is visible on screen.
// ------------------------------------------------------------------

const DEVICE_CLASSES = [
  { id: 'all',     label: 'All Devices', icon: Monitor },
  { id: 'desktop', label: 'Desktop',     icon: Monitor,    hint: '>1024px' },
  { id: 'tablet',  label: 'Tablet',      icon: Tablet,     hint: '768–1024px' },
  { id: 'mobile',  label: 'Mobile',      icon: Smartphone, hint: '<768px' },
];

// ------------------------------------------------------------------
const HeatmapVisualization = ({
  data,
  loading,
  pageUrl,
  teamId,
  websiteId,
  timeFilter,
  stripQueryParams = false,
  className = '',
}) => {
  // 'clicks' | 'scroll' | 'attention' — which heatmap tab is active
  const [activeTab, setActiveTab] = useState('clicks');
  // Scroll depth data fetched from /events/heatmap/scroll-depth
  const [scrollDepthData, setScrollDepthData] = useState(null);
  const [loadingScrollDepth, setLoadingScrollDepth] = useState(false);
  // Scroll depth overlay on the click heatmap canvas
  const [showScrollDepthOverlay, setShowScrollDepthOverlay] = useState(false);
  // Attention data (top elements by time_in_viewport_ms)
  const [attentionData, setAttentionData] = useState(null);
  const [loadingAttention, setLoadingAttention] = useState(false);
  // Hover heatmap data (aggregated from page_summary hover_grid / move_grid)
  // Used when layerMode is 'hover' or 'move' inside the Heatmap tab.
  const [hoverHeatmapData, setHoverHeatmapData] = useState(null);
  const [loadingHoverHeatmap, setLoadingHoverHeatmap] = useState(false);

  const [selectedEventTypes, setSelectedEventTypes] = useState([]);
  const [selectedDeviceClass, setSelectedDeviceClass] = useState('all');
  const [filteredData, setFilteredData]   = useState(null);
  const [loadingFiltered, setLoadingFiltered] = useState(false);
  const [showEventFilter, setShowEventFilter] = useState(false);
  const [iframeBlocked, setIframeBlocked] = useState(false);
  // Actual rendered height of the iframe content (post-load); starts at docHeight
  // but is updated once the iframe reports its own scrollHeight so the canvas
  // covers the full page rather than stopping at an arbitrary docHeight value.
  const [iframeContentHeight, setIframeContentHeight] = useState(null);
  const [showFoldLine, setShowFoldLine] = useState(true);
  // layerMode: 'all' | 'rage' | 'dead' | 'form' — controls colour scheme and event filter
  const [layerMode, setLayerMode] = useState('all');
  // highlightSelector: when non-null, grid cells whose topSelector matches are highlighted
  const [highlightSelector, setHighlightSelector] = useState(null);

  // scale: CSS scale applied to the logical block so it fits the container width
  const [scale, setScale] = useState(1);

  const scrollWrapRef      = useRef(null);
  const canvasRef          = useRef(null);
  const attentionCanvasRef = useRef(null);
  const filterDropRef      = useRef(null);
  const iframeRef          = useRef(null);
  // Timers for delayed height re-reads after iframe onLoad (catches SPA hydration).
  const readTimersRef      = useRef([]);

  // ----------------------------------------------------------------
  // Derived display data
  // ----------------------------------------------------------------
  const displayData = filteredData || data;

  // The logical width the iframe should render at.
  // When hover/move mode is active the dimensions come from the hover data;
  // otherwise use the click heatmap data. Falls back to 1280.
  const logicalW = useMemo(() => {
    if ((layerMode === 'hover' || layerMode === 'move') && hoverHeatmapData) {
      const w = hoverHeatmapData.mode_viewport_width;
      return w && w > 100 ? w : 1280;
    }
    const w = displayData?.mode_viewport_width;
    return w && w > 100 ? w : 1280;
  }, [displayData, hoverHeatmapData, layerMode]);

  // coordHeight: the height used for ALL canvas/dot coordinate math.
  // Switches to hover data dimensions when hover/move layer is active.
  const coordHeight = useMemo(() => {
    if ((layerMode === 'hover' || layerMode === 'move') && hoverHeatmapData) {
      return hoverHeatmapData.document_height ?? 3000;
    }
    return displayData?.document_height ?? 3000;
  }, [displayData, hoverHeatmapData, layerMode]);

  // iframeDisplayHeight: how tall to actually render the iframe and scroll area.
  // Starts at coordHeight (backend estimate), then grows when we measure a taller live page.
  // Decoupling this from coordHeight means the full page is always visible even when the
  // backend estimate is smaller than the real page — the canvas still covers coordHeight
  // and dots land correctly, but the page below that is visible in the iframe.
  const iframeDisplayHeight = iframeContentHeight
    ? Math.max(coordHeight, iframeContentHeight)
    : coordHeight;


  // Reset per-page state when the URL or time filter changes
  useEffect(() => {
    readTimersRef.current.forEach(t => clearTimeout(t));
    readTimersRef.current = [];
    setIframeContentHeight(null);
    setHoverHeatmapData(null); // force re-fetch for new page
  }, [pageUrl, timeFilter, selectedDeviceClass]);

  // Listen for dimensions from the embedded page (when it has our SDK and is in an iframe).
  // This allows cross-origin heatmap previews to report their real height so we size correctly.
  useEffect(() => {
    if (!pageUrl) return;
    let expectedOrigin = null;
    try {
      expectedOrigin = new URL(pageUrl).origin;
    } catch (_) {
      return;
    }
    const onMessage = (event) => {
      const d = event.data;
      if (d?.type !== 'cryptique_heatmap_dimensions') return;
      if (event.origin !== expectedOrigin) return;
      if (typeof d.document_height !== 'number') return;
      // Reject out-of-range values — browser scroll-animation libs can report 16 777 216 (2^24).
      if (d.document_height < 200 || d.document_height > 25000) return;
      // Only grow — never shrink (page content never disappears after load).
      setIframeContentHeight(prev => {
        const h = Math.round(d.document_height);
        return (prev === null || h > prev) ? h : prev;
      });
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [pageUrl]);

  // ----------------------------------------------------------------
  // Responsive scale: keep logicalW fitting the scrollWrap's width
  // ----------------------------------------------------------------
  useEffect(() => {
    const el = scrollWrapRef.current;
    if (!el) return;

    const update = () => {
      const availW = el.clientWidth;
      if (availW > 0) setScale(availW / logicalW);
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [logicalW]);

  // Re-compute scale whenever logicalW changes (different device class selected)
  useEffect(() => {
    const el = scrollWrapRef.current;
    if (!el) return;
    const availW = el.clientWidth;
    if (availW > 0) setScale(availW / logicalW);
  }, [logicalW]);

  // ----------------------------------------------------------------
  // Event type filter
  // ----------------------------------------------------------------
  const availableEventTypes = useMemo(() => data?.eventTypes ?? [], [data]);

  useEffect(() => {
    if (availableEventTypes.length > 0 && selectedEventTypes.length === 0) {
      setSelectedEventTypes([...availableEventTypes]);
    }
  }, [availableEventTypes]);

  // ----------------------------------------------------------------
  // Fetch filtered heatmap (event-type filter OR device-class filter)
  // ----------------------------------------------------------------
  const fetchFilteredHeatmap = useCallback(async (eventTypes, deviceClass) => {
    if (!pageUrl || !timeFilter || !teamId) return;
    setLoadingFiltered(true);
    try {
      const p = new URLSearchParams({
        team_id: teamId,
        url: pageUrl,
        grid_size: 40,
      });
      if (websiteId)        p.set('website_id', websiteId);
      if (timeFilter?.from) p.set('from', timeFilter.from);
      if (timeFilter?.to)   p.set('to',   timeFilter.to);
      if (eventTypes && eventTypes.length > 0) {
        p.set('event_names', JSON.stringify(eventTypes));
      }
      if (deviceClass && deviceClass !== 'all') {
        p.set('device_class', deviceClass);
      }
      if (stripQueryParams) {
        p.set('strip_query_params', 'true');
      }

      const res = await axiosInstance.get(`/events/heatmap?${p.toString()}`);
      if (res.data.success) setFilteredData(res.data);
    } catch (err) {
      console.error('Error fetching filtered heatmap:', err);
    } finally {
      setLoadingFiltered(false);
    }
  }, [pageUrl, timeFilter, teamId, websiteId, stripQueryParams]);

  // Fetch scroll depth data when scroll tab is active OR overlay is enabled on click tab
  useEffect(() => {
    const needScrollDepth = activeTab === 'scroll' || (activeTab === 'clicks' && showScrollDepthOverlay);
    if (!needScrollDepth || !pageUrl || !teamId) return;
    const fetchScrollDepth = async () => {
      setLoadingScrollDepth(true);
      try {
        const p = new URLSearchParams({ team_id: teamId, url: pageUrl });
        if (websiteId)        p.set('website_id', websiteId);
        if (timeFilter?.from) p.set('from', timeFilter.from);
        if (timeFilter?.to)   p.set('to',   timeFilter.to);
        if (selectedDeviceClass !== 'all') p.set('device_class', selectedDeviceClass);
        if (stripQueryParams) p.set('strip_query_params', 'true');
        const res = await axiosInstance.get(`/events/heatmap/scroll-depth?${p.toString()}`);
        if (res.data.success) setScrollDepthData(res.data);
      } catch (err) {
        console.error('Error fetching scroll depth:', err);
      } finally {
        setLoadingScrollDepth(false);
      }
    };
    fetchScrollDepth();
  }, [activeTab, showScrollDepthOverlay, pageUrl, teamId, websiteId, timeFilter, selectedDeviceClass, stripQueryParams]);

  // Fetch attention data (top elements by dwell time) when attention tab is active
  useEffect(() => {
    if (activeTab !== 'attention' || !pageUrl || !teamId) return;
    const fetch = async () => {
      setLoadingAttention(true);
      try {
        const p = new URLSearchParams({ team_id: teamId, url: pageUrl });
        if (websiteId)                      p.set('website_id', websiteId);
        if (timeFilter?.from)               p.set('from', timeFilter.from);
        if (timeFilter?.to)                 p.set('to',   timeFilter.to);
        if (stripQueryParams)               p.set('strip_query_params', 'true');
        if (selectedDeviceClass !== 'all')  p.set('device_class', selectedDeviceClass);
        const res = await axiosInstance.get(`/events/heatmap/attention?${p.toString()}`);
        if (res.data.success) setAttentionData(res.data);
      } catch (err) {
        console.error('Error fetching attention data:', err);
      } finally {
        setLoadingAttention(false);
      }
    };
    fetch();
  }, [activeTab, pageUrl, teamId, websiteId, timeFilter, stripQueryParams, selectedDeviceClass]);

  // Fetch hover heatmap data when hover or move layer is selected in the Heatmap tab
  useEffect(() => {
    const needHover = activeTab === 'clicks' && (layerMode === 'hover' || layerMode === 'move');
    if (!needHover || !pageUrl || !teamId) return;
    if (hoverHeatmapData) return; // already loaded for this page/filter combination
    const fetch = async () => {
      setLoadingHoverHeatmap(true);
      try {
        const p = new URLSearchParams({ team_id: teamId, url: pageUrl });
        if (websiteId)        p.set('website_id', websiteId);
        if (timeFilter?.from) p.set('from', timeFilter.from);
        if (timeFilter?.to)   p.set('to',   timeFilter.to);
        if (stripQueryParams) p.set('strip_query_params', 'true');
        const res = await axiosInstance.get(`/events/heatmap/hover?${p.toString()}`);
        if (res.data.success) setHoverHeatmapData(res.data);
      } catch (err) {
        console.error('Error fetching hover heatmap data:', err);
      } finally {
        setLoadingHoverHeatmap(false);
      }
    };
    fetch();
  }, [activeTab, layerMode, pageUrl, teamId, websiteId, timeFilter, stripQueryParams]);

  // When layerMode changes, snap event-type selection to the relevant types.
  // Hover/move modes use a separate data source (hoverHeatmapData) so event-type
  // selection is irrelevant — keep all selected to avoid an unnecessary re-fetch.
  const handleLayerModeChange = useCallback((mode) => {
    setLayerMode(mode);
    setHighlightSelector(null);
    if (mode === 'rage') {
      const types = availableEventTypes.filter(t => t === 'rage_click');
      if (types.length > 0) setSelectedEventTypes(types);
    } else if (mode === 'dead') {
      const types = availableEventTypes.filter(t => t === 'dead_click');
      if (types.length > 0) setSelectedEventTypes(types);
    } else if (mode === 'form') {
      const formTypes = ['form_submit', 'input_change', 'form_focus'];
      const types = availableEventTypes.filter(t => formTypes.includes(t));
      if (types.length > 0) setSelectedEventTypes(types);
      else setSelectedEventTypes([...availableEventTypes]);
    } else {
      // 'all', 'hover', 'move' — restore full event type selection
      setSelectedEventTypes([...availableEventTypes]);
    }
  }, [availableEventTypes]);

  // Re-fetch whenever event-type selection or device class changes
  useEffect(() => {
    const allSelected   = selectedEventTypes.length === availableEventTypes.length;
    const noneSelected  = selectedEventTypes.length === 0;
    const noDeviceFilter = selectedDeviceClass === 'all';

    if (noneSelected) {
      setFilteredData(null);
      return;
    }

    if (allSelected && noDeviceFilter) {
      // No filtering needed — use raw data from parent
      setFilteredData(data);
    } else {
      fetchFilteredHeatmap(
        allSelected ? null : selectedEventTypes,
        selectedDeviceClass,
      );
    }
  }, [selectedEventTypes, selectedDeviceClass, data, availableEventTypes]);

  // ----------------------------------------------------------------
  // Canvas drawing — two-pass offscreen kernel
  //
  // Pass 1 (offscreen): draw alpha-only intensity blobs onto a
  //   separate canvas using globalCompositeOperation='lighter' so
  //   overlapping hot-spots accumulate correctly rather than washing out.
  // Pass 2 (main canvas): read each pixel's alpha from the offscreen
  //   canvas and map it through a colour ramp to get the final RGBA.
  //   This produces a clean heat gradient without the "overexposed"
  //   look of directly stacking semi-transparent radial gradients.
  //
  // Dead-click mode also draws a small X marker at each cell centre
  // so zero-result areas are visually distinct from hot spots.
  //
  // layerMode colour ramps:
  //   'all'   → blue → cyan → yellow → red  (standard heat)
  //   'rage'  → light-orange → deep-red
  //   'dead'  → light-grey  → purple
  //   'form'  → light-green → dark-green
  //   'hover' → amber → orange → deep warm-red  (hover dwell time)
  //   'move'  → dark-blue → cyan → light-teal   (mouse movement density)
  // ----------------------------------------------------------------
  const drawHeatmap = useCallback((ctx, grid, gridSize, maxCount, canvasW, canvasH, mode = 'all') => {
    if (!grid.length || maxCount <= 0) return;

    const cellW  = canvasW / gridSize;
    const cellH  = canvasH / gridSize;
    // Hover/move: anchor radius to cell width so blobs stay element-sized even on
    // tall pages where cellH >> cellW. Click modes keep the original formula.
    const isMovementMode = mode === 'hover' || mode === 'move';
    const radius = isMovementMode
      ? cellW * 1.5
      : Math.max(cellW, cellH) * 0.75;

    // ── Pass 1: build intensity map on offscreen canvas ──────────────
    const offscreen = document.createElement('canvas');
    offscreen.width  = canvasW;
    offscreen.height = canvasH;
    const octx = offscreen.getContext('2d');
    octx.clearRect(0, 0, canvasW, canvasH);
    octx.globalCompositeOperation = 'lighter';  // accumulate intensities

    grid.forEach(cell => {
      const intensity = Math.min(1, cell.count / maxCount);
      const cx = (cell.x + 0.5) * cellW;
      const cy = (cell.y + 0.5) * cellH;

      const grad = octx.createRadialGradient(cx, cy, 0, cx, cy, radius);
      if (isMovementMode) {
        // Tight core with steep falloff — precise element-level hotspots
        grad.addColorStop(0,    `rgba(255,255,255,${intensity})`);
        grad.addColorStop(0.25, `rgba(255,255,255,${intensity * 0.5})`);
        grad.addColorStop(0.6,  `rgba(255,255,255,${intensity * 0.1})`);
        grad.addColorStop(1,    'rgba(255,255,255,0)');
      } else {
        grad.addColorStop(0,   `rgba(255,255,255,${intensity})`);
        grad.addColorStop(0.5, `rgba(255,255,255,${intensity * 0.3})`);
        grad.addColorStop(1,   'rgba(255,255,255,0)');
      }

      octx.fillStyle = grad;
      octx.beginPath();
      octx.arc(cx, cy, radius, 0, Math.PI * 2);
      octx.fill();
    });

    // ── Pass 2: map alpha values through colour ramp onto main canvas ─
    const imageData = octx.getImageData(0, 0, canvasW, canvasH);
    const src = imageData.data;

    const out = ctx.createImageData(canvasW, canvasH);
    const dst = out.data;

    for (let i = 0; i < src.length; i += 4) {
      const a = src[i + 3] / 255;   // normalised intensity 0–1 (alpha channel, not R)
      if (a < 0.01) continue;   // skip transparent pixels

      let r, g, b, finalA;

      if (mode === 'rage') {
        // light-orange (low) → deep-red (high)
        r = 255;
        g = Math.floor(Math.max(0, 165 - a * 165));
        b = 0;
        finalA = Math.floor((0.35 + a * 0.6) * 255);
      } else if (mode === 'dead') {
        // light-grey (low) → purple (high)
        r = Math.floor(Math.max(0, 180 - a * 60));
        g = Math.floor(Math.max(0, 170 - a * 120));
        b = Math.floor(Math.min(255, 190 + a * 65));
        finalA = Math.floor((0.35 + a * 0.6) * 255);
      } else if (mode === 'form') {
        // light-green (low) → dark-green (high)
        r = Math.floor(Math.max(0, 50  - a * 40));
        g = Math.floor(Math.min(255, 180 + a * 50));
        b = Math.floor(Math.max(0, 80  - a * 70));
        finalA = Math.floor((0.35 + a * 0.6) * 255);
      } else if (mode === 'hover') {
        // amber (low) → orange → deep warm-red (high) — distinguishable from click red
        if (a < 0.5) {
          const t = a * 2;
          r = 255;
          g = Math.floor(200 - t * 100);  // 200 → 100
          b = Math.floor(60  - t * 60);   // 60  → 0
        } else {
          const t = (a - 0.5) * 2;
          r = 255;
          g = Math.floor(100 - t * 100);  // 100 → 0
          b = 0;
        }
        finalA = Math.floor((0.4 + a * 0.6) * 255);
      } else if (mode === 'move') {
        // dark-blue (low) → cyan (mid) → light-teal (high) — distinct from hover/click
        if (a < 0.5) {
          const t = a * 2;
          r = Math.floor(20  + t * 10);   // 20 → 30
          g = Math.floor(80  + t * 150);  // 80 → 230
          b = Math.floor(180 + t * 75);   // 180 → 255
        } else {
          const t = (a - 0.5) * 2;
          r = Math.floor(30  + t * 80);   // 30 → 110
          g = Math.floor(230 + t * 25);   // 230 → 255
          b = Math.floor(255 - t * 80);   // 255 → 175
        }
        finalA = Math.floor((0.4 + a * 0.6) * 255);
      } else {
        // Standard: blue → cyan → yellow → red
        if (a < 0.5) {
          const t = a * 2;
          r = Math.floor(t * 255);
          g = Math.floor(100 + t * 155);
          b = Math.floor(255 - t * 255);
        } else {
          const t = (a - 0.5) * 2;
          r = 255;
          g = Math.floor(255 - t * 255);
          b = 0;
        }
        finalA = Math.floor((0.3 + a * 0.65) * 255);
      }

      dst[i]     = r;
      dst[i + 1] = g;
      dst[i + 2] = b;
      dst[i + 3] = finalA;
    }

    ctx.clearRect(0, 0, canvasW, canvasH);
    ctx.putImageData(out, 0, 0);

    // ── Dead-click mode: draw X markers at each cell centre ──────────
    if (mode === 'dead') {
      ctx.save();
      ctx.strokeStyle = 'rgba(220,38,38,0.85)';
      ctx.lineWidth = Math.max(1.5, cellW * 0.08);
      const xSize = Math.min(cellW, cellH) * 0.3;
      grid.forEach(cell => {
        const cx = (cell.x + 0.5) * cellW;
        const cy = (cell.y + 0.5) * cellH;
        ctx.beginPath();
        ctx.moveTo(cx - xSize, cy - xSize);
        ctx.lineTo(cx + xSize, cy + xSize);
        ctx.moveTo(cx + xSize, cy - xSize);
        ctx.lineTo(cx - xSize, cy + xSize);
        ctx.stroke();
      });
      ctx.restore();
    }
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Select the correct grid for the active layer mode.
    // Hover/move modes use hoverHeatmapData; all other modes use click data.
    const isHoverMode = layerMode === 'hover' || layerMode === 'move';
    const activeGrid = isHoverMode
      ? (layerMode === 'hover' ? hoverHeatmapData?.hoverGrid : hoverHeatmapData?.moveGrid)
      : displayData?.grid;

    if (!activeGrid?.length) {
      // Clear canvas if no data (e.g. hover data not yet loaded)
      const ctx = canvas.getContext('2d');
      canvas.width  = logicalW;
      canvas.height = coordHeight;
      ctx.clearRect(0, 0, logicalW, coordHeight);
      return;
    }

    // Canvas dimensions match the coordinate space (API document_height), NOT the live
    // iframe height. This keeps grid cell pixel positions consistent with the yPct values
    // computed on the backend using the same document_height.
    canvas.width  = logicalW;
    canvas.height = coordHeight;

    const ctx      = canvas.getContext('2d');
    // Use gridSize from API response; fall back to 40 (the value both callers request).
    const gridSize = (isHoverMode ? hoverHeatmapData?.gridSize : displayData?.gridSize) || 40;
    const maxCount = Math.max(...activeGrid.map(c => c.count), 1);
    drawHeatmap(ctx, activeGrid, gridSize, maxCount, logicalW, coordHeight, layerMode);

    // Draw fold line: a dashed horizontal line at mode_viewport_height showing where most
    // users' visible area ends. Content below this line requires scrolling to be seen.
    if (showFoldLine && displayData.mode_viewport_height > 0) {
      const foldY = displayData.mode_viewport_height;
      if (foldY < coordHeight) {
        ctx.save();
        ctx.setLineDash([8, 6]);
        ctx.lineWidth = 2;
        ctx.strokeStyle = 'rgba(99, 102, 241, 0.85)';
        ctx.beginPath();
        ctx.moveTo(0, foldY);
        ctx.lineTo(logicalW, foldY);
        ctx.stroke();
        // Label
        ctx.setLineDash([]);
        ctx.font = 'bold 12px system-ui, sans-serif';
        ctx.fillStyle = 'rgba(99,102,241,0.95)';
        ctx.fillRect(8, foldY - 18, 136, 18);
        ctx.fillStyle = '#fff';
        ctx.fillText('↑ Above fold (most users)', 12, foldY - 5);
        ctx.restore();
      }
    }

    // Draw scroll depth overlay: semi-transparent red bands showing drop-off zones,
    // with dashed tick lines and percentage labels at every 20% depth.
    if (showScrollDepthOverlay && scrollDepthData?.buckets?.length > 0) {
      scrollDepthData.buckets.forEach(bucket => {
        const y1 = (bucket.depth_from / 100) * coordHeight;
        const y2 = (bucket.depth_to   / 100) * coordHeight;
        // Opacity increases as fewer users reach this depth (drop-off = red tint)
        const dropout = Math.max(0, 1 - bucket.pct_of_sessions / 100);
        ctx.save();
        ctx.fillStyle = `rgba(239,68,68,${(dropout * 0.35).toFixed(2)})`;
        ctx.fillRect(0, y1, logicalW, y2 - y1);
        ctx.restore();
      });
      // Tick lines + labels at every 20% depth boundary
      scrollDepthData.buckets.forEach(bucket => {
        if (bucket.depth_from % 20 !== 0) return;
        const y = (bucket.depth_from / 100) * coordHeight;
        ctx.save();
        ctx.setLineDash([6, 4]);
        ctx.lineWidth   = 1;
        ctx.strokeStyle = 'rgba(100,116,139,0.55)';
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(logicalW, y); ctx.stroke();
        ctx.setLineDash([]);
        const label = `${bucket.depth_from}% — ${bucket.pct_of_sessions}% of sessions`;
        ctx.font      = 'bold 11px system-ui,sans-serif';
        const lw      = ctx.measureText(label).width + 10;
        ctx.fillStyle = 'rgba(255,255,255,0.88)';
        ctx.fillRect(logicalW - lw - 4, y + 3, lw, 16);
        ctx.fillStyle = 'rgba(15,23,42,0.85)';
        ctx.fillText(label, logicalW - lw, y + 15);
        ctx.restore();
      });
    }

    // Draw highlight overlay: when a row in the top elements table is selected,
    // outline all grid cells whose topSelector matches in bright cyan.
    if (highlightSelector && displayData.grid.length > 0) {
      const cellW = logicalW / gridSize;
      const cellH = coordHeight / gridSize;
      ctx.save();
      ctx.strokeStyle = 'rgba(6,182,212,0.95)';
      ctx.fillStyle   = 'rgba(6,182,212,0.18)';
      ctx.lineWidth   = 2;
      displayData.grid.forEach(cell => {
        if (cell.topSelector === highlightSelector) {
          const px = cell.x * cellW;
          const py = cell.y * cellH;
          ctx.fillRect(px, py, cellW, cellH);
          ctx.strokeRect(px + 1, py + 1, cellW - 2, cellH - 2);
        }
      });
      ctx.restore();
    }
  }, [displayData, hoverHeatmapData, coordHeight, logicalW, drawHeatmap, showFoldLine, layerMode,
      highlightSelector, showScrollDepthOverlay, scrollDepthData]);

  // ----------------------------------------------------------------
  // Attention overlay — draw heat-coloured element rectangles on the
  // attention canvas based on avg page-relative position data.
  // ----------------------------------------------------------------
  useEffect(() => {
    const canvas = attentionCanvasRef.current;
    if (!canvas || !attentionData?.elements?.length) return;

    const attCoordH = attentionData.document_height || coordHeight;
    const attLogW   = attentionData.mode_viewport_width || logicalW;

    canvas.width  = attLogW;
    canvas.height = attCoordH;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, attLogW, attCoordH);

    const elements = attentionData.elements.filter(el => el.avg_page_y > 0);
    if (!elements.length) return;

    const maxDwell = elements[0]?.total_dwell_ms || 1; // sorted desc by backend

    elements.forEach(el => {
      const t     = Math.min(1, el.total_dwell_ms / maxDwell);
      // Heat colour: blue (low) → orange (mid) → red (high)
      let r, g, b;
      if (t < 0.5) {
        const u = t * 2;
        r = Math.floor(30  + u * 225);  // 30 → 255
        g = Math.floor(100 + u * 65);   // 100 → 165
        b = Math.floor(255 - u * 255);  // 255 → 0
      } else {
        const u = (t - 0.5) * 2;
        r = 255;
        g = Math.floor(165 - u * 165);  // 165 → 0
        b = 0;
      }
      const alpha = 0.35 + t * 0.35; // 0.35 – 0.70

      const x  = el.avg_page_x  || 0;
      const y  = el.avg_page_y  || 0;
      const w  = Math.max(el.avg_elem_width  || 0, 40);
      const h  = Math.max(el.avg_elem_height || 0, 16);

      ctx.fillStyle   = `rgba(${r},${g},${b},${alpha.toFixed(2)})`;
      ctx.strokeStyle = `rgba(${r},${g},${b},${Math.min(1, alpha + 0.3).toFixed(2)})`;
      ctx.lineWidth   = 1.5;
      ctx.beginPath();
      ctx.roundRect ? ctx.roundRect(x, y, w, h, 4) : ctx.rect(x, y, w, h);
      ctx.fill();
      ctx.stroke();
    });
  }, [attentionData, coordHeight, logicalW]);

  // ----------------------------------------------------------------
  // Dropdown outside-click
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!showEventFilter) return;
    const handler = e => {
      if (filterDropRef.current && !filterDropRef.current.contains(e.target))
        setShowEventFilter(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showEventFilter]);

  // ----------------------------------------------------------------
  // Device breakdown counts
  // ----------------------------------------------------------------
  const deviceBreakdown = useMemo(() => data?.device_breakdown || {}, [data]);

  // ----------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------
  if (loading && !displayData) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4b57aa] mx-auto mb-4" />
          <p className="text-sm text-[#475569]">Loading heatmap…</p>
        </div>
      </div>
    );
  }

  if (!displayData) return null;

  // Visual (post-scale) dimensions — these drive the scroll container size.
  // Use iframeDisplayHeight so the scroll area covers the full rendered page.
  const visualW = logicalW * scale;
  const visualH = iframeDisplayHeight * scale;

  return (
    <div className={`flex flex-col min-h-0 ${className}`.trim()}>

      {/* ── Top bar: stats + filters ── */}
      <div className="flex-shrink-0 bg-white border border-gray-200 rounded-lg p-4 mb-3">
        <div className="flex items-center justify-between flex-wrap gap-3">

          {/* Stats */}
          <div className="flex items-center gap-6">
            {/* Tab switcher */}
            <div className="flex items-center gap-1 border border-gray-200 rounded-lg p-1 bg-gray-50">
              {[
                { id: 'clicks',    label: 'Heatmap'      },
                { id: 'attention', label: 'Attention'    },
                { id: 'scroll',    label: 'Scroll Depth' },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'bg-white text-[#4b57aa] shadow-sm border border-gray-200'
                      : 'text-[#475569] hover:text-[#0f172a]'
                  }`}
                  style={{ fontFamily: 'Raleway, sans-serif' }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div>
              <div className="text-xs text-[#475569] mb-0.5">Total Events</div>
              <div className="text-2xl font-semibold text-[#0f172a]">
                {displayData.totalEvents?.toLocaleString() ?? 0}
              </div>
            </div>
            <div>
              <div className="text-xs text-[#475569] mb-0.5">Event Types</div>
              <div className="text-2xl font-semibold text-[#0f172a]">
                {availableEventTypes.length}
              </div>
            </div>
            <div>
              <div className="text-xs text-[#475569] mb-0.5">Preview Width</div>
              <div className="text-2xl font-semibold text-[#0f172a]">
                {logicalW}px
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* ── Layer quick-select: click types + hover/movement ── */}
            <style>{`
              @keyframes cq-pulse-rage {
                0%,100% { box-shadow: 0 0 0 0 rgba(234,88,12,0); }
                50%      { box-shadow: 0 0 0 4px rgba(234,88,12,0.4); }
              }
              .cq-rage-active { animation: cq-pulse-rage 1.6s ease-in-out infinite; }
            `}</style>
            {activeTab === 'clicks' && (
            <div className="flex items-center gap-1 border border-gray-200 rounded-lg p-1 bg-gray-50">
              {/* Click signal types */}
              {[
                { id: 'all',  label: 'All',  color: 'text-[#4b57aa]' },
                { id: 'rage', label: 'Rage', color: 'text-orange-600' },
                { id: 'dead', label: 'Dead', color: 'text-purple-600' },
                { id: 'form', label: 'Form', color: 'text-green-700'  },
              ].map(layer => (
                <button
                  key={layer.id}
                  onClick={() => handleLayerModeChange(layer.id)}
                  className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                    layerMode === layer.id
                      ? `bg-white ${layer.color} shadow-sm border border-gray-200${layer.id === 'rage' ? ' cq-rage-active' : ''}`
                      : 'text-[#475569] hover:text-[#0f172a]'
                  }`}
                  style={{ fontFamily: 'Raleway, sans-serif' }}
                >
                  {layer.label}
                </button>
              ))}
              {/* Separator between click types and movement types */}
              <div className="w-px h-5 bg-gray-300 mx-1 self-center" />
              {/* Movement signal types */}
              {[
                { id: 'hover', label: 'Hover Dwell',     color: 'text-amber-600' },
                { id: 'move',  label: 'Mouse Movement',  color: 'text-blue-600'  },
              ].map(layer => (
                <button
                  key={layer.id}
                  onClick={() => handleLayerModeChange(layer.id)}
                  className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                    layerMode === layer.id
                      ? `bg-white ${layer.color} shadow-sm border border-gray-200`
                      : 'text-[#475569] hover:text-[#0f172a]'
                  }`}
                  style={{ fontFamily: 'Raleway, sans-serif' }}
                >
                  {layer.label}
                </button>
              ))}
            </div>
            )}

            {/* ── Device class segmented control ── */}
            <div className="flex items-center gap-1 border border-gray-200 rounded-lg p-1 bg-gray-50">
              {DEVICE_CLASSES.map(dc => {
                const IconComp = dc.icon;
                const count = dc.id !== 'all'
                  ? (deviceBreakdown[dc.id] ?? 0)
                  : (data?.totalEvents ?? 0);
                return (
                  <button
                    key={dc.id}
                    onClick={() => setSelectedDeviceClass(dc.id)}
                    title={dc.hint ?? dc.label}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                      selectedDeviceClass === dc.id
                        ? 'bg-white text-[#4b57aa] shadow-sm border border-gray-200'
                        : 'text-[#475569] hover:text-[#0f172a]'
                    }`}
                    style={{ fontFamily: 'Raleway, sans-serif' }}
                  >
                    <IconComp size={14} />
                    <span>{dc.label}</span>
                    {count > 0 && (
                      <span className={`text-xs ${selectedDeviceClass === dc.id ? 'text-[#4b57aa]' : 'text-[#94a3b8]'}`}>
                        ({count.toLocaleString()})
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* ── Event-type multiselect (hidden for hover/move layers — different data source) ── */}
            {(layerMode !== 'hover' && layerMode !== 'move') && <div className="relative" ref={filterDropRef}>
              <button
                onClick={() => setShowEventFilter(v => !v)}
                className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                style={{ fontFamily: 'Raleway, sans-serif' }}
              >
                <Icon name="filter" size={18} color="#475569" />
                <span className="text-[#0f172a]">
                  Event Types ({selectedEventTypes.length}/{availableEventTypes.length})
                </span>
              </button>

              {showEventFilter && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-gray-200 shadow-xl rounded-lg z-50 max-h-80 overflow-y-auto">
                  <div className="p-3 border-b border-gray-200 flex items-center justify-between">
                    <span className="text-sm font-semibold text-[#0f172a]">Filter by Event Type</span>
                    <button onClick={() => setShowEventFilter(false)} className="text-[#475569] hover:text-[#0f172a]">
                      <X size={16} />
                    </button>
                  </div>
                  <div className="p-3 border-b border-gray-200 flex gap-2">
                    <button onClick={() => setSelectedEventTypes([...availableEventTypes])}
                      className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50">
                      Select All
                    </button>
                    <button onClick={() => setSelectedEventTypes([])}
                      className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50">
                      Deselect All
                    </button>
                  </div>
                  <div className="p-2">
                    {availableEventTypes.map(et => (
                      <label key={et} className="flex items-center gap-2 px-2 py-2 hover:bg-gray-50 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedEventTypes.includes(et)}
                          onChange={() => setSelectedEventTypes(prev =>
                            prev.includes(et) ? prev.filter(x => x !== et) : [...prev, et]
                          )}
                          className="w-4 h-4 text-[#4b57aa] border-gray-300 rounded"
                        />
                        <span className="text-sm text-[#0f172a]">{et}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>}

            {/* ── Fold line toggle ── */}
            {displayData?.mode_viewport_height > 0 && (
              <button
                onClick={() => setShowFoldLine(v => !v)}
                title={`${showFoldLine ? 'Hide' : 'Show'} fold line at ${displayData.mode_viewport_height}px`}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs border rounded-lg transition-colors ${
                  showFoldLine
                    ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                    : 'border-gray-300 bg-white text-[#475569] hover:bg-gray-50'
                }`}
                style={{ fontFamily: 'Raleway, sans-serif' }}
              >
                <span className="w-4 h-0.5 inline-block border-t-2 border-dashed border-current" />
                <span>Fold line</span>
              </button>
            )}

            {/* ── Scroll depth overlay toggle (clicks tab only) ── */}
            <button
              onClick={() => setShowScrollDepthOverlay(v => !v)}
              title="Overlay scroll depth drop-off zones on the click heatmap"
              className={`flex items-center gap-1.5 px-3 py-2 text-xs border rounded-lg transition-colors ${
                showScrollDepthOverlay
                  ? 'border-red-300 bg-red-50 text-red-700'
                  : 'border-gray-300 bg-white text-[#475569] hover:bg-gray-50'
              }`}
              style={{ fontFamily: 'Raleway, sans-serif' }}
            >
              <span className="w-4 h-3 inline-block rounded-sm" style={{
                background: showScrollDepthOverlay
                  ? 'linear-gradient(to bottom,rgba(239,68,68,0.1),rgba(239,68,68,0.7))'
                  : 'linear-gradient(to bottom,rgba(100,116,139,0.1),rgba(100,116,139,0.5))',
              }} />
              <span>Scroll overlay</span>
            </button>
          </div>
        </div>
      </div>

      {/* ── Scroll Depth tab ── */}
      {activeTab === 'scroll' && (
        <div className="flex-1 min-h-0 flex flex-col gap-4">
          {loadingScrollDepth && (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4b57aa]" />
            </div>
          )}
          {!loadingScrollDepth && scrollDepthData && (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-[#0f172a]" style={{ fontFamily: 'Raleway, sans-serif' }}>
                  Scroll Depth Funnel
                </h3>
                <p className="text-xs text-[#475569] mt-0.5">
                  {scrollDepthData.totalSessions.toLocaleString()} total sessions tracked
                </p>
              </div>
              <div className="p-5 space-y-3">
                {scrollDepthData.buckets.map(bucket => (
                  <div key={bucket.depth_from} className="flex items-center gap-3">
                    <div className="w-16 text-right text-xs text-[#475569] shrink-0">
                      {bucket.depth_from}–{bucket.depth_to}%
                    </div>
                    <div className="flex-1 relative h-7 bg-gray-100 rounded overflow-hidden">
                      <div
                        className="absolute inset-y-0 left-0 rounded transition-all"
                        style={{
                          width: `${bucket.pct_of_sessions}%`,
                          background: bucket.pct_of_sessions >= 60
                            ? 'rgba(34,197,94,0.8)'
                            : bucket.pct_of_sessions >= 30
                              ? 'rgba(234,179,8,0.8)'
                              : 'rgba(239,68,68,0.8)',
                        }}
                      />
                      <span className="absolute inset-y-0 left-2 flex items-center text-xs font-semibold text-white" style={{ textShadow: '0 0 4px rgba(0,0,0,0.5)' }}>
                        {bucket.pct_of_sessions}% of sessions
                      </span>
                    </div>
                    <div className="w-20 text-xs text-[#475569] shrink-0">
                      {bucket.sessions_reached.toLocaleString()} sessions
                    </div>
                  </div>
                ))}
              </div>
              <div className="px-5 py-3 border-t border-gray-100 text-xs text-[#94a3b8]">
                % of sessions (that generated scroll events) that reached at least this depth. Green = strong engagement, red = drop-off zone.
              </div>
            </div>
          )}
          {!loadingScrollDepth && !scrollDepthData && (
            <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg text-sm">
              No scroll data found for this page and time range. Ensure the SDK is installed and auto-events are enabled.
            </div>
          )}
        </div>
      )}

      {/* ── Attention tab: visual heatmap overlay + top elements table ── */}
      {activeTab === 'attention' && (
        <div className="flex-1 min-h-0 flex flex-col gap-4">
          {loadingAttention && (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4b57aa]" />
            </div>
          )}

          {/* ── Attention heatmap overlay: iframe + canvas ── */}
          {!loadingAttention && attentionData && attentionData.elements.filter(e => e.avg_page_y > 0).length > 0 && (() => {
            const attLogW   = attentionData.mode_viewport_width || 1280;
            const attCoordH = attentionData.document_height || 3000;
            const attScale  = Math.min(1, (window.innerWidth * 0.85) / attLogW); // fit in viewport
            return (
              <div className="flex-shrink-0 bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col" style={{ minHeight: 400 }}>
                <div className="flex-shrink-0 px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                  <div>
                    <span className="text-sm font-semibold text-[#0f172a]" style={{ fontFamily: 'Raleway, sans-serif' }}>Attention Heatmap</span>
                    <span className="ml-2 text-xs text-[#475569]">
                      Elements coloured by dwell time — blue (low) → red (high)
                    </span>
                  </div>
                  <span className="text-xs text-[#94a3b8]">
                    {attentionData.totalSessions.toLocaleString()} sessions · {attLogW}px preview
                  </span>
                </div>
                {/* Scroll container: simple overflow-auto, no transform scaling */}
                <div className="overflow-auto" style={{ maxHeight: 600, position: 'relative' }}>
                  <div style={{ width: attLogW * attScale, height: attCoordH * attScale, position: 'relative' }}>
                    <div style={{
                      position: 'absolute', top: 0, left: 0,
                      width: attLogW, height: attCoordH,
                      transformOrigin: 'top left', transform: `scale(${attScale})`
                    }}>
                      <iframe
                        src={pageUrl}
                        title="Attention heatmap preview"
                        scrolling="no"
                        style={{ position: 'absolute', top: 0, left: 0, width: attLogW, height: attCoordH, border: 'none', overflow: 'hidden' }}
                        sandbox="allow-same-origin allow-scripts"
                        referrerPolicy="no-referrer"
                      />
                      <canvas
                        ref={attentionCanvasRef}
                        width={attLogW}
                        height={attCoordH}
                        style={{ position: 'absolute', top: 0, left: 0, width: attLogW, height: attCoordH, pointerEvents: 'none' }}
                        aria-hidden
                      />
                    </div>
                  </div>
                </div>
                {/* Attention legend */}
                <div className="flex-shrink-0 px-5 py-2 border-t border-gray-100 flex items-center gap-3">
                  <span className="text-xs text-[#475569]">Attention:</span>
                  <div className="flex items-center gap-2">
                    <div className="w-32 h-3 rounded" style={{
                      background: 'linear-gradient(to right,rgba(30,100,255,.35),rgba(255,165,0,.6),rgba(255,0,0,.75))',
                    }} />
                    <span className="text-xs text-[#475569]">Low → High dwell</span>
                  </div>
                </div>
              </div>
            );
          })()}

          {!loadingAttention && attentionData && attentionData.elements.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-[#0f172a]" style={{ fontFamily: 'Raleway, sans-serif' }}>
                  Top Elements by Attention
                </h3>
                <p className="text-xs text-[#475569] mt-0.5">
                  Total time each element was visible in users' viewports · {attentionData.totalSessions.toLocaleString()} sessions tracked
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-t border-gray-100">
                  <thead>
                    <tr className="bg-gray-50 text-[#475569]">
                      <th className="px-4 py-2 text-left font-medium">Element</th>
                      <th className="px-4 py-2 text-left font-medium">Tag</th>
                      <th className="px-4 py-2 text-left font-medium">Text</th>
                      <th className="px-4 py-2 text-right font-medium">Total Attention</th>
                      <th className="px-4 py-2 text-right font-medium">Avg per View</th>
                      <th className="px-4 py-2 text-right font-medium">Sessions</th>
                      <th className="px-4 py-2 text-right font-medium">% Sessions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attentionData.elements.map((el, i) => {
                      const totalSec  = (el.total_dwell_ms / 1000).toFixed(1);
                      const avgSec    = (el.avg_dwell_ms   / 1000).toFixed(1);
                      const barWidth  = Math.round((el.total_dwell_ms / attentionData.elements[0].total_dwell_ms) * 100);
                      return (
                        <tr key={el.selector + i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                          <td className="px-4 py-2 font-mono text-[#4b57aa] max-w-[200px] truncate" title={el.selector}>
                            {el.selector || '—'}
                          </td>
                          <td className="px-4 py-2 text-[#64748b]">{el.tag}</td>
                          <td className="px-4 py-2 text-[#0f172a] max-w-[200px] truncate" title={el.text}>
                            {el.text || <span className="text-[#94a3b8] italic">—</span>}
                          </td>
                          <td className="px-4 py-2 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <div className="w-24 h-2 bg-gray-100 rounded overflow-hidden">
                                <div className="h-full rounded" style={{
                                  width: `${barWidth}%`,
                                  background: 'linear-gradient(to right,rgba(245,158,11,0.6),rgba(234,88,12,0.9))',
                                }} />
                              </div>
                              <span className="font-semibold text-[#0f172a] w-12 text-right">{totalSec}s</span>
                            </div>
                          </td>
                          <td className="px-4 py-2 text-right text-[#475569]">{avgSec}s</td>
                          <td className="px-4 py-2 text-right text-[#475569]">{el.session_count.toLocaleString()}</td>
                          <td className="px-4 py-2 text-right">
                            <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                              el.pct_of_sessions >= 30 ? 'bg-amber-100 text-amber-700' : 'text-[#64748b]'
                            }`}>
                              {el.pct_of_sessions}%
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-5 py-3 border-t border-gray-100 text-xs text-[#94a3b8]">
                Total attention = sum of time each element was visible in the viewport across all tracked sessions. Requires element_view auto-events.
              </div>
            </div>
          )}
          {!loadingAttention && attentionData && attentionData.elements.length === 0 && (
            <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg text-sm">
              No element view data found for this page and time range. Ensure the SDK is installed and auto-events are enabled.
            </div>
          )}
          {!loadingAttention && !attentionData && (
            <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg text-sm">
              Loading attention data…
            </div>
          )}
        </div>
      )}

      {/* ── Heatmap tab (clicks + hover dwell + mouse movement) ── */}
      {activeTab === 'clicks' && (
      <>
      {/* ── Hover/move loading state ── */}
      {(layerMode === 'hover' || layerMode === 'move') && loadingHoverHeatmap && (
        <div className="flex items-center justify-center h-48">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4b57aa] mx-auto mb-2" />
            <p className="text-xs text-[#475569]">Loading {layerMode === 'hover' ? 'hover dwell' : 'mouse movement'} data…</p>
          </div>
        </div>
      )}
      {/* ── No event type selected (click modes only) ── */}
      {(layerMode !== 'hover' && layerMode !== 'move') && selectedEventTypes.length === 0 ? (
        <div className="flex-shrink-0 bg-white border border-gray-200 rounded-lg p-8 text-center">
          <p className="text-sm text-[#475569]">Please select at least one event type to view the heatmap.</p>
        </div>
      ) : (!loadingHoverHeatmap && (
        <div className="flex-1 min-h-0 flex flex-col bg-white border border-gray-200 rounded-lg overflow-hidden">
          {/* Meta row */}
          <div className="flex-shrink-0 px-5 py-3 border-b border-gray-100 flex items-center justify-between">
            <p className="text-xs text-[#475569] break-all">
              <span className="font-medium text-[#0f172a]">Page: </span>{pageUrl}
            </p>
            <p className="text-xs text-[#475569] whitespace-nowrap ml-4">
              {(layerMode === 'hover' || layerMode === 'move')
                ? `${(layerMode === 'hover' ? hoverHeatmapData?.hoverGrid : hoverHeatmapData?.moveGrid)?.length ?? 0} cells`
                : `${displayData.grid?.length ?? 0} cells · ${displayData.totalEvents?.toLocaleString() ?? 0} events`}
              {selectedDeviceClass !== 'all' && (
                <span className="ml-2 px-1.5 py-0.5 bg-[#4b57aa]/10 text-[#4b57aa] rounded text-xs capitalize">
                  {selectedDeviceClass}
                </span>
              )}
            </p>
          </div>

          {/*
            ── Scroll container ──────────────────────────────────────────
            KEY FIX: CSS transform:scale() does NOT affect layout size.
            If we put the scaled block (position:absolute, height:docHeight)
            inside overflow-auto directly, the container's scrollHeight
            = docHeight (unscaled layout), while visually the content is
            only docHeight*scale tall — causing scroll to go too far.

            Solution: wrap the scaled block in a "sizer" div whose explicit
            height = docHeight * scale  (the true visual height).
            The scaled block is absolute inside the sizer, so it visually
            fills the sizer exactly and the scroll area is always correct.
          ──────────────────────────────────────────────────────────────── */}
          <div
            ref={scrollWrapRef}
            className="flex-1 min-h-0 overflow-auto"
            style={{ position: 'relative' }}
          >
            {loadingFiltered && (
              <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-20">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4b57aa]" />
              </div>
            )}

            {/*
              Sizer: exact visual dimensions after scaling.
              This is what drives the scrollable area — it's in normal flow
              so overflow-auto respects it correctly.
            */}
            <div style={{ width: visualW, height: visualH, position: 'relative', flexShrink: 0 }}>
              {/*
                Logical block: full unscaled size, scaled down from top-left.
                position:absolute keeps it from affecting the sizer's layout.
              */}
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: logicalW,
                  // iframeDisplayHeight so the block grows with the live page.
                  // Canvas inside is coordHeight (dot positioning) which may be shorter.
                  height: iframeDisplayHeight,
                  transformOrigin: 'top left',
                  transform: `scale(${scale})`,
                }}
              >
                {/* iframe: renders the site at logicalW wide (= mode viewport width).
                    Height is iframeDisplayHeight which grows beyond coordHeight when the
                    live page is taller than the backend estimate. Canvas stays at
                    coordHeight so dot percentages remain correctly anchored. */}
                <iframe
                  ref={iframeRef}
                  src={pageUrl}
                  title="Page preview"
                  scrolling="no"
                  style={{
                    position: 'absolute',
                    top: 0, left: 0,
                    width:  logicalW,
                    height: iframeDisplayHeight,
                    border: 'none',
                    overflow: 'hidden',
                  }}
                  sandbox="allow-same-origin allow-scripts"
                  referrerPolicy="no-referrer"
                  onError={() => setIframeBlocked(true)}
                  onLoad={() => {
                    setIframeBlocked(false);
                    // Clear any previous delayed reads from a prior load.
                    readTimersRef.current.forEach(t => clearTimeout(t));
                    readTimersRef.current = [];

                    const tryRead = () => {
                      try {
                        const el = iframeRef.current;
                        if (!el) return;
                        const iDoc = el.contentDocument || el.contentWindow?.document;
                        if (!iDoc) return;
                        const h = Math.max(
                          iDoc.body?.scrollHeight || 0,
                          iDoc.documentElement?.scrollHeight || 0,
                        );
                        // Reject out-of-range; only grow (SPA content accumulates, never shrinks).
                        if (h > 200 && h < 25000) {
                          setIframeContentHeight(prev => (prev === null || h > prev) ? h : prev);
                        }
                      } catch (_) {
                        // Cross-origin — SDK postMessage bridge handles height for these.
                      }
                    };

                    // Immediate read, then delayed reads to catch SPA hydration which
                    // renders content after the initial load event fires.
                    tryRead();
                    readTimersRef.current = [
                      setTimeout(tryRead, 500),
                      setTimeout(tryRead, 1500),
                      setTimeout(tryRead, 3000),
                    ];
                  }}
                />

                {iframeBlocked && (
                  <div style={{
                    position: 'absolute', top: 0, left: 0,
                    width: logicalW, height: iframeDisplayHeight,
                    background: '#f1f5f9',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 14, color: '#64748b', padding: 24, textAlign: 'center',
                  }}>
                    This page cannot be embedded (X-Frame-Options). Heatmap overlay is still shown.
                  </div>
                )}

                {/* Canvas: coordinate space = API document_height (coordHeight).
                    This must NOT change when iframeContentHeight updates, because
                    all yPct values were normalised against document_height on the backend. */}
                <canvas
                  ref={canvasRef}
                  width={logicalW}
                  height={coordHeight}
                  style={{
                    position: 'absolute',
                    top: 0, left: 0,
                    width:  logicalW,
                    height: coordHeight,
                    pointerEvents: 'none',
                  }}
                  aria-hidden
                />
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="flex-shrink-0 px-5 py-3 border-t border-gray-100 flex items-center gap-4 flex-wrap">
            <span className="text-xs text-[#475569]">Intensity:</span>
            <div className="flex items-center gap-2">
              <div className="w-32 h-3 rounded" style={{
                background: layerMode === 'rage'
                  ? 'linear-gradient(to right,rgba(255,165,0,.4),rgba(255,60,0,1))'
                  : layerMode === 'dead'
                    ? 'linear-gradient(to right,rgba(180,170,190,.4),rgba(120,50,255,.9))'
                    : layerMode === 'form'
                      ? 'linear-gradient(to right,rgba(50,180,80,.3),rgba(20,130,30,1))'
                      : layerMode === 'hover'
                        ? 'linear-gradient(to right,rgba(255,200,60,.4),rgba(255,100,0,.7),rgba(255,0,0,1))'
                        : layerMode === 'move'
                          ? 'linear-gradient(to right,rgba(20,80,180,.4),rgba(30,230,255,.7),rgba(110,255,175,1))'
                          : 'linear-gradient(to right,rgba(0,100,255,.3),rgba(255,255,0,.8),rgba(255,0,0,1))',
              }} />
              <span className="text-xs text-[#475569]">
                {layerMode === 'hover' ? 'Short → Long dwell' : layerMode === 'move' ? 'Sparse → Dense movement' : 'Low → High'}
              </span>
            </div>
            <span className="text-xs text-[#94a3b8] ml-auto">
              {layerMode === 'hover' || layerMode === 'move'
                ? `${hoverHeatmapData?.totalSessions?.toLocaleString() ?? '—'} page_summary events · `
                : ''}
              Preview at {logicalW}px width
              {data?.mode_viewport_width ? ' (most common user viewport)' : ''}
              {iframeContentHeight
                ? ` · page height ${iframeContentHeight}px (live)`
                : ` · page height ${coordHeight}px (estimated)`}
            </span>
          </div>
        </div>
      ))}

      {/* ── Top Clicked Elements table (clicks tab only) ── */}
      {activeTab === 'clicks' && displayData?.topElements?.length > 0 && (
        <div className="flex-shrink-0 bg-white border border-gray-200 rounded-lg mt-3 overflow-hidden">
          <details>
            <summary
              className="px-5 py-3 text-sm font-semibold text-[#0f172a] cursor-pointer select-none flex items-center justify-between list-none"
              style={{ fontFamily: 'Raleway, sans-serif' }}
            >
              <span>
                Top Clicked Elements
                <span className="ml-2 text-xs font-normal text-[#475569]">
                  ({displayData.topElements.length} elements · {displayData.totalSessions?.toLocaleString() ?? '?'} sessions)
                </span>
              </span>
              <div className="flex items-center gap-2">
                {highlightSelector && (
                  <span className="text-xs text-cyan-600 font-medium">
                    Highlighting: <code className="font-mono">{highlightSelector}</code>
                    <button
                      onClick={e => { e.preventDefault(); setHighlightSelector(null); }}
                      className="ml-1 text-[#94a3b8] hover:text-[#475569]"
                    >✕</button>
                  </span>
                )}
                <span className="text-[#94a3b8] text-xs">▼</span>
              </div>
            </summary>
            <div className="px-4 py-2 text-xs text-[#94a3b8] border-b border-gray-100">
              Click a row to highlight matching grid cells on the heatmap above.
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-t border-gray-100">
                <thead>
                  <tr className="bg-gray-50 text-[#475569]">
                    <th className="px-4 py-2 text-left font-medium">Selector</th>
                    <th className="px-4 py-2 text-left font-medium">Tag</th>
                    <th className="px-4 py-2 text-left font-medium">Text</th>
                    <th className="px-4 py-2 text-right font-medium">Clicks</th>
                    <th className="px-4 py-2 text-right font-medium">Sessions</th>
                    <th className="px-4 py-2 text-right font-medium">% Sessions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayData.topElements.map((el, i) => {
                    const isHighlighted = highlightSelector === el.selector;
                    return (
                      <tr
                        key={el.selector + i}
                        onClick={() => setHighlightSelector(prev => prev === el.selector ? null : el.selector)}
                        className={`cursor-pointer transition-colors ${
                          isHighlighted
                            ? 'bg-cyan-50 ring-1 ring-inset ring-cyan-300'
                            : i % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50/50 hover:bg-gray-100/60'
                        }`}
                      >
                        <td className={`px-4 py-2 font-mono max-w-[200px] truncate ${isHighlighted ? 'text-cyan-700' : 'text-[#4b57aa]'}`} title={el.selector}>
                          {el.selector}
                        </td>
                        <td className="px-4 py-2 text-[#64748b]">{el.tag}</td>
                        <td className="px-4 py-2 text-[#0f172a] max-w-[240px] truncate" title={el.text}>
                          {el.text || <span className="text-[#94a3b8] italic">—</span>}
                        </td>
                        <td className="px-4 py-2 text-right font-semibold text-[#0f172a]">
                          {el.count.toLocaleString()}
                        </td>
                        <td className="px-4 py-2 text-right text-[#475569]">
                          {el.sessionCount.toLocaleString()}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                            el.pctOfSessions >= 30 ? 'bg-[#4b57aa]/10 text-[#4b57aa]' : 'text-[#64748b]'
                          }`}>
                            {el.pctOfSessions}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </details>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default HeatmapVisualization;
